import { combineReducers } from 'redux'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { ByObjectData, ByObjectQuery, StatisticsData } from '../../../responses/statistics'
import { none, of, Option } from 'fp-ts/Option'
import { RootEpic } from '../../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import {
    HideConfiguration,
    OpenConfiguration,
    SortConfiguration
} from '../../../components/pages/statistics/StatisticsTable/configuration'
import { ID } from '../../../types/UtilityTypes'
import { User } from '../../../responses/users'
import { DepartmentWithUsers } from '../../../responses/departments'
import { WorkObject } from '../../../responses/objects'
import { payloadReducer } from '../../../utils/redux-utils'
import { SortDirection } from '../../../constants/direction'
import {RequestStatus} from '../../../responses/response'

export const byObjectActions = {
    data: createAsyncAction(
        '@STATISTICS_BY_OBJECT/DATA_REQUEST',
        '@STATISTICS_BY_OBJECT/DATA_SUCCESS',
        '@STATISTICS_BY_OBJECT/DATA_FAILURE',
    )<ByObjectQuery, StatisticsData<ByObjectData>, void>(),
    setManagers: createAction('@STATISTICS_BY_OBJECT/SET_MANAGERS')<User[]>(),
    setDepartments: createAction('@STATISTICS_BY_OBJECT/SET_DEPARTMENTS')<DepartmentWithUsers[]>(),
    setUsers: createAction('@STATISTICS_BY_OBJECT/SET_USERS')<User[]>(),
    setWorkObjects: createAction('@STATISTICS_BY_OBJECT/SET_WORK_OBJECTS')<WorkObject[]>(),
    setHiddenColumns: createAction('@STATISTICS_BY_OBJECT/SET_HIDDEN_COLUMNS')<HideConfiguration[]>(),
    setSort: createAction('@STATISTICS_BY_OBJECT/SET_SORT')<SortConfiguration>(),
    toggleRow: createAction('@STATISTICS_BY_OBJECT/TOGGLE_ROW')<Record<ID, OpenConfiguration>>(),
    clearFilters: createAction('@STATISTICS_BY_MANAGER/CLEAR_FILTERS')()
}

export interface ByObjectState {
    data: Option<StatisticsData<ByObjectData>>,
    hide: HideConfiguration[],
    open: Record<ID, OpenConfiguration>,
    sort: SortConfiguration,
    managers: User[],
    users: User[],
    departments: DepartmentWithUsers[],
    workObjects: WorkObject[],
    state: RequestStatus,
}

export const byObjectReducer = combineReducers<ByObjectState>({
    data: createReducer<Option<StatisticsData<ByObjectData>>>(none)
        .handleAction(byObjectActions.data.success, (_, action) => of(action.payload)),
    managers: createReducer<User[]>([])
        .handleAction(byObjectActions.setManagers, payloadReducer)
        .handleAction(byObjectActions.clearFilters, () => []),
    users: createReducer<User[]>([])
        .handleAction(byObjectActions.setUsers, payloadReducer)
        .handleAction(byObjectActions.clearFilters, () => []),
    departments: createReducer<DepartmentWithUsers[]>([])
        .handleAction(byObjectActions.setDepartments, payloadReducer)
        .handleAction(byObjectActions.clearFilters, () => []),
    workObjects: createReducer<WorkObject[]>([])
        .handleAction(byObjectActions.setWorkObjects, payloadReducer)
        .handleAction(byObjectActions.clearFilters, () => []),
    hide: createReducer<HideConfiguration[]>([])
        .handleAction(byObjectActions.setHiddenColumns, payloadReducer),
    open: createReducer<Record<ID, OpenConfiguration>>({})
        .handleAction(byObjectActions.data.success, () => ({}))
        .handleAction(byObjectActions.toggleRow, payloadReducer),
    sort: createReducer<SortConfiguration>({group: '', name: '', direction: SortDirection.ASC})
        .handleAction(byObjectActions.setSort, payloadReducer),
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(byObjectActions.data.request, () => RequestStatus.FETCHING)
        .handleAction(byObjectActions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(byObjectActions.data.failure, () => RequestStatus.FAILURE),
})

export const byObjectEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byObjectActions.data.request)),
    mergeMap(action => apiRequests.statistics.byObject({query: action.payload})),
    map(res => res.response.success?
        byObjectActions.data.success(res.response):
        byObjectActions.data.failure(toastFailedResponse(res.response, 'Загрузка статистики по объекту не удалась'))
    )
)